import { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import { FaSearch } from "react-icons/fa"
import { toast } from "sonner"
import { AppDispatch } from "../../store"

import { deleteIcon, editIcon } from "../../asset"
import ECSLoader from "../../components/common/EcsLoader"
import Pagination from "../../components/common/Pagination"
import { useDebounce } from "../../util/hooks/useDebounce"
import { DbConnection } from "../../types/databaseTypes"
import { deleteDBConnection, fetchDBConnections } from "../../features/database/dbConnectionServices"

export function DBList() {
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()

    const [connections, setConnections] = useState<DbConnection[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")
    const [pageSize, setPageSize] = useState(50)
    const [page, setPage] = useState(0)
    const [pageInfo, setPageInfo] = useState<any>({})

    const [filter, setFilter] = useState("")
    const debouncedFilter = useDebounce(filter, 500)

    const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string } | null>(null)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => { setPage(0) }, [debouncedFilter, pageSize])

    const loadConnections = async () => {
        setLoading(true)
        setError("")
        try {
            const result = await dispatch(fetchDBConnections()).unwrap()
            const filtered = debouncedFilter
                ? result.filter(c =>
                    c.databaseName.toLowerCase().includes(debouncedFilter.toLowerCase()) ||
                    c.host.toLowerCase().includes(debouncedFilter.toLowerCase()) ||
                    c.dbType.toLowerCase().includes(debouncedFilter.toLowerCase())
                )
                : result
            const start = page * pageSize
            setConnections(filtered.slice(start, start + pageSize))
            setPageInfo({ total: filtered.length, index: page, size: pageSize, count: Math.ceil(filtered.length / pageSize) })
        } catch (err: any) {
            setError(err?.message || "Failed to load database connections")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => { loadConnections() }, [dispatch, page, pageSize, debouncedFilter])

    const handleEdit = (item: DbConnection) => {
        navigate("/admin/userDB/connect-db", { state: { editData: item } })
    }

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return
        setDeleting(true)
        try {
            await dispatch(deleteDBConnection(deleteTarget.id)).unwrap()
            toast.success("Connection deleted successfully!")
            setDeleteTarget(null)
            loadConnections()
        } catch (err: any) {
            toast.error(err?.message || "Failed to delete connection")
        } finally {
            setDeleting(false)
        }
    }

    const totalPages = pageInfo.count ?? 0

    return (
        <>
            <div className="bg-white rounded-3xl p-4 sm:p-5 mt-5 w-full relative">

                <ECSLoader show={loading} />

                {error && (
                    <div className="text-red-500 mb-3 text-sm font-medium">{error}</div>
                )}

                {/* Top Actions */}
                <div className="flex flex-wrap justify-end items-center gap-2 mb-4">
                    <Pagination
                        page={page}
                        setPage={setPage}
                        totalPages={totalPages}
                        totalItems={pageInfo.total || 0}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        loading={loading}
                    />
                </div>

                {/* Search — mobile */}
                <div className="flex gap-2 mb-3 sm:hidden">
                    <SearchBox
                        value={filter}
                        onChange={setFilter}
                        placeholder="Search database, host, type"
                    />
                </div>

                {/* ── DESKTOP TABLE ── */}
                <div className={`hidden sm:block overflow-x-auto transition-opacity duration-300 ${loading ? "opacity-40 pointer-events-none" : "opacity-100"}`}>
                    <table className="w-full table-fixed text-sm border-separate border-spacing-y-2">
                        <thead className="text-gray-600">
                            <tr>
                                <th className="p-2 text-left font-bold w-16">ID</th>
                                <th className="p-2 text-left font-bold w-28">Type</th>
                                <th className="p-2 text-left font-bold">Database</th>
                                <th className="p-2 text-left font-bold">Host</th>
                                <th className="p-2 text-left font-bold w-24">Port</th>
                                <th className="p-2 text-left font-bold">Username</th>
                                <th className="p-2 text-left font-bold w-24">Actions</th>
                            </tr>
                            <tr>
                                <th /><th />
                                <th>
                                    <SearchBox
                                        value={filter}
                                        onChange={setFilter}
                                        placeholder="Search"
                                    />
                                </th>
                                <th /><th /><th /><th />
                            </tr>
                        </thead>
                        <tbody>
                            {connections.length === 0 && !loading ? (
                                <tr>
                                    <td colSpan={7} className="text-center py-8 text-gray-400">
                                        No database connections found.
                                    </td>
                                </tr>
                            ) : (
                                connections.map((item, index) => (
                                    <tr
                                        key={item.id ?? index}
                                        className="font-medium even:bg-gray-50 odd:bg-white hover:bg-gray-100 transition-colors"
                                    >
                                        <td className="p-2">{item.id}</td>
                                        <td className="p-2">
                                            <DbTypeBadge type={item.dbType} />
                                        </td>
                                        <td className="p-2 truncate">{item.databaseName}</td>
                                        <td className="p-2 truncate">{item.host}</td>
                                        <td className="p-2">{item.port}</td>
                                        <td className="p-2 truncate">{item.username}</td>
                                        <td className="p-3 flex gap-3">
                                            <img
                                                src={editIcon}
                                                alt="Edit"
                                                className="w-4 h-4 cursor-pointer"
                                                onClick={() => handleEdit(item)}
                                            />
                                            <img
                                                src={deleteIcon}
                                                alt="Delete"
                                                className="w-4 h-4 cursor-pointer"
                                                onClick={() => setDeleteTarget({ id: item.id, name: item.databaseName })}
                                            />
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* ── MOBILE CARD LIST ── */}
                <div className={`flex flex-col gap-3 sm:hidden transition-opacity duration-300 ${loading ? "opacity-40 pointer-events-none" : "opacity-100"}`}>
                    {connections.length === 0 && !loading ? (
                        <div className="text-center py-8 text-gray-400 text-sm">No database connections found.</div>
                    ) : (
                        connections.map((item, index) => (
                            <div
                                key={item.id ?? index}
                                className="rounded-2xl border border-gray-100 bg-gray-50 p-4"
                            >
                                <div className="flex items-start justify-between gap-2 mb-3">
                                    <div className="min-w-0">
                                        <p className="font-bold text-gray-800 text-sm truncate">{item.databaseName}</p>
                                        <p className="text-xs text-gray-400">ID: {item.id}</p>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        <DbTypeBadge type={item.dbType} />
                                        <div className="flex gap-2 ml-1">
                                            <img
                                                src={editIcon}
                                                alt="Edit"
                                                className="w-4 h-4 cursor-pointer"
                                                onClick={() => handleEdit(item)}
                                            />
                                            <img
                                                src={deleteIcon}
                                                alt="Delete"
                                                className="w-4 h-4 cursor-pointer"
                                                onClick={() => setDeleteTarget({ id: item.id, name: item.databaseName })}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                                    <CardField label="Host" value={item.host} truncate />
                                    <CardField label="Port" value={String(item.port)} />
                                    <CardField label="Username" value={item.username} />
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Delete Confirmation Popup */}
            {deleteTarget && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div
                        className="absolute inset-0 bg-web-background/40 backdrop-blur-sm"
                        onClick={() => !deleting && setDeleteTarget(null)}
                    />
                    <div className="relative bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm mx-4 flex flex-col gap-4">
                        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mx-auto">
                            <img src={deleteIcon} alt="Delete" className="w-5 h-5" />
                        </div>
                        <div className="text-center">
                            <h3 className="text-lg font-bold text-gray-800">Delete Connection</h3>
                            <p className="text-sm text-gray-500 mt-1">
                                Are you sure you want to delete{" "}
                                <span className="font-semibold text-gray-700">{deleteTarget.name}</span>
                                ? This action cannot be undone.
                            </p>
                        </div>
                        <div className="flex gap-3 mt-2">
                            <button
                                onClick={() => setDeleteTarget(null)}
                                disabled={deleting}
                                className="flex-1 py-2 rounded-xl border border-gray-200 text-gray-600 font-semibold hover:bg-gray-50 transition disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteConfirm}
                                disabled={deleting}
                                className="flex-1 py-2 rounded-xl bg-red-500 text-white font-semibold hover:bg-red-600 transition disabled:opacity-60"
                            >
                                {deleting ? "Deleting..." : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

// ── Small helpers ──────────────────────────────────────────────────────────────

function DbTypeBadge({ type }: { type: string }) {
    const isMySQL = type?.toUpperCase() === "MYSQL"
    return (
        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full ${isMySQL ? "bg-blue-50 text-blue-600" : "bg-purple-50 text-purple-600"}`}>
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isMySQL ? "bg-blue-500" : "bg-purple-500"}`} />
            {isMySQL ? "MySQL" : "PostgreSQL"}
        </span>
    )
}

function CardField({ label, value, truncate }: { label: string; value?: string; truncate?: boolean }) {
    return (
        <div className="min-w-0">
            <span className="text-gray-400 font-medium">{label}: </span>
            <span className={`text-gray-700 font-semibold ${truncate ? "block truncate" : ""}`}>{value || "—"}</span>
        </div>
    )
}

function SearchBox({
    value = "",
    onChange,
    placeholder = "Search",
}: {
    value?: string
    onChange?: (val: string) => void
    placeholder?: string
}) {
    return (
        <div className="relative w-full max-w-[200px]">
            <input
                value={value}
                onChange={e => onChange?.(e.target.value)}
                type="text"
                placeholder={placeholder}
                className="w-full border rounded-md h-8 pl-8 pr-2 text-xs font-medium outline-none focus:ring-1 focus:ring-blue-400"
            />
            <FaSearch className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
        </div>
    )
}